import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import PDFPage from './PDFDocument';

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const styles = StyleSheet.create({
  clientHeader: {
    backgroundColor: '#4F5F4B',
    color: 'white',
    padding: 15,
    textAlign: 'center',
    borderRadius: 8,
    marginBottom: 10,
  },
  clientName: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  startingBalance: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    fontStyle: 'italic',
    padding: 10,
    marginBottom: 15,
  },
  mainContentContainer: {
    flexDirection: 'row',
  },
  summaryContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 10,
    width: '90%',
  },
  columnContainer: {
    flex: 1,
    marginHorizontal: 5,
  },
  additionalNeedsColumn: {
    flex: 0.2,
  },
  incomeColumn: {
    flex: 0.6,
  },
  growthColumn: {
    flex: 0.2,
  },
  columnTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 5,
  },
  columnAmount: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
  },
  legContainer: {
    flexDirection: 'row',
    gap: 5,
  },
  legBox: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    borderTopWidth: 3,
    borderTopColor: '#4F5F4B',
    padding: 8,
    borderRadius: 4,
    marginHorizontal: 2,
  },
  legTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 5,
  },
  legDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    marginVertical: 3,
  },
  legSection: {
    marginVertical: 5,
    textAlign: 'center',
  },
  legSectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  legSectionValue: {
    fontSize: 14,
  },
  legIncomeValue: {
    fontSize: 11,
    fontWeight: 'bold',
  },
  emergencyBox: {
    backgroundColor: '#f5f5f5',
    borderTopWidth: 3,
    borderTopColor: '#BFA58A',
    padding: 10,
    borderRadius: 4,
    textAlign: 'center',
  },
  growthBox: {
    backgroundColor: '#f5f5f5',
    borderTopWidth: 3,
    borderTopColor: '#2F2B28',
    padding: 10,
    borderRadius: 4,
    textAlign: 'center',
  },
  chartContainer: {
    width: '30%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 10,
  },
  chartImage: {
    width: '100%',
    height: 'auto',
    maxWidth: 350,
    maxHeight: 350,
  },
  fallbackText: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
});

const IncomePlanPDF = ({ clientData, chartImages = {}, pageNumber }) => {
  const chartImage = chartImages['income-plan-chart'];

  const getYearsUntilActivating = (legNumber) => {
    return Number(clientData[`leg${legNumber}_years_deferred`]) || 0;
  };

  const renderLegBox = (legNum) => {
  const product = clientData.plan_names
    ? clientData[`leg${legNum}_product`]
    : `Leg ${legNum}`;

  const principal = clientData[`leg${legNum}_principle`] || 0;
  const annualIncome = clientData[`leg${legNum}AnnualIncome`] || 0;

  return (
    <View key={`leg-${legNum}`} style={styles.legBox}>
      <Text style={styles.legTitle}>{product}</Text>
      <View style={styles.legDivider} />

      <View style={styles.legSection}>
        <Text style={styles.legSectionTitle}>Principal</Text>
        <Text style={styles.legSectionValue}>{formatCurrency(principal)}</Text>
      </View>

      <View style={styles.legSection}>
        <Text style={styles.legSectionTitle}>Years Until{`\n`}Activating</Text>
        <Text style={styles.legSectionValue}>{getYearsUntilActivating(legNum)}</Text>
      </View>

      <View style={styles.legSection}>
        <Text style={styles.legSectionTitle}>Monthly Income</Text>
        <Text style={styles.legSectionValue}>
          {formatCurrency(annualIncome / 12)}
        </Text>
      </View>

      <View style={styles.legSection}>
        <Text style={styles.legSectionTitle}>Annual Income</Text>
        <Text style={styles.legIncomeValue}>
          {formatCurrency(annualIncome)}
        </Text>
      </View>
    </View>
  );
};

  return (
    <PDFPage 
      header="Redesigned Income Plan"
      pageNumber={pageNumber}
      showDisclaimer={true}
      showFooter={true}
    >
      <Text style={styles.startingBalance}>
        Starting Balance: {formatCurrency(clientData.totalRetAssets)}
      </Text>
      
      <View style={styles.mainContentContainer}>
        <View style={styles.summaryContainer}>
          {/* Additional Needs Column */}
          <View style={[styles.columnContainer, styles.additionalNeedsColumn]}>
            <Text style={styles.columnTitle}>Add. Needs</Text>
            <Text style={styles.columnAmount}>{formatCurrency(clientData.emergencyFunds)}</Text>
            
            <View style={styles.emergencyBox}>
              <Text style={styles.legSectionTitle}>Emergency Funds Set Aside</Text>
              <Text style={styles.legSectionValue}>{formatCurrency(clientData.emergencyFunds)}</Text>
            </View>
          </View>
          
          {/* Income Column */}
          <View style={[styles.columnContainer, styles.incomeColumn]}>
            <Text style={styles.columnTitle}>Income</Text>
            <Text style={styles.columnAmount}>{formatCurrency(clientData.income_bucket)}</Text>
            
            <View style={styles.legContainer}>
              {/* Leg 1 */}
              {renderLegBox(1)}
              
              {/* Leg 2 */}
              {renderLegBox(2)}
              
              {/* Leg 3 */}
              {renderLegBox(3)}
            </View>
          </View>
          
          {/* Growth Column */}
          <View style={[styles.columnContainer, styles.growthColumn]}>
            <Text style={styles.columnTitle}>Growth</Text>
            <Text style={styles.columnAmount}>{formatCurrency(clientData.growthPrincipal || 0)}</Text>
            
            <View style={styles.growthBox}>
              <Text style={styles.legSectionTitle}>Starting{`\n`}Principal</Text>
              <Text style={styles.legSectionValue}>{formatCurrency(clientData.growthPrincipal || 0)}</Text>
              
              <View style={styles.legSection}>
                <Text style={styles.legSectionTitle}>Assumed Net Growth</Text>
                <Text style={styles.legSectionValue}>{clientData.assumed_net_growth || 0}%</Text>
              </View>
              
              <View style={styles.legSection}>
                <Text style={styles.legSectionTitle}>Years to Grow</Text>
                <Text style={styles.legSectionValue}>{clientData.years_to_grow || 0}</Text>
              </View>
            </View>
          </View>
        </View>
        
        {/* Chart */}
        <View style={styles.chartContainer}>
          {chartImage ? (
            <Image src={chartImage} style={styles.chartImage} />
          ) : (
            <Text style={styles.fallbackText}>
              (No chart image available)
            </Text>
          )}
        </View>
      </View>
    </PDFPage>
  );
};

export default IncomePlanPDF;